import { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import { useWindowDimensions, usePermission } from '@kiper/hooks';

const iosDimensions = {
  width: 480,
  height: 640,
  aspectRatio: 1.33,
};

const defaultDimensions = {
  width: 300,
  height: 400,
  aspectRatio: 1,
};

const mobileDimensions = {
  width: null,
  height: null,
  aspectRatio: null,
};

const useWebcamCapture = () => {
  const permissionsCamera = usePermission('camera');
  const { requestPermission } = permissionsCamera;

  const { width: windowWidth } = useWindowDimensions();

  const [widthValue, setWidthValue] = useState(300);
  const [heightValue, setHeightValue] = useState(400);
  const [aspectValue, setAspectValue] = useState(1);
  const [userMediaError, setUserMediaError] = useState(null);

  const webcamRef = useRef(null);

  useEffect(() => {
    requestPermission({
      video: true,
    });
  }, [requestPermission]);

  const capturePhoto = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      return imageSrc;
    }
    return null;
  }, []);

  const videoConstraints = {
    facingMode: 'user',
    width: widthValue,
    height: heightValue,
    aspectRatio: aspectValue,
  };

  const isIOS = useMemo(() => {
    const userAgent =
      navigator?.userAgent || navigator?.vendor || window?.opera;
    return /iPad|iPhone|iPod/i.test(userAgent);
  }, []);

  const handleDimension = ({ width, height, aspectRatio }) => {
    setHeightValue(height);
    setWidthValue(width);
    setAspectValue(aspectRatio);
  };

  useEffect(() => {
    if (isIOS) {
      handleDimension(iosDimensions);
    } else if (windowWidth <= 1024) {
      handleDimension(mobileDimensions);
    } else {
      handleDimension(defaultDimensions);
    }
  }, [isIOS, windowWidth]);

  const handleUserMediaError = error => {
    setUserMediaError(error?.name || 'Unknown error');
  };

  const handleTryAgain = () => {
    setUserMediaError(null);
    requestPermission({
      video: true,
    });
  };

  return {
    webcamRef,
    userMediaError,
    videoConstraints,
    capturePhoto,
    handleTryAgain,
    handleUserMediaError,
  };
};

export default useWebcamCapture;
